import React, { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import type { IConsultingFormValues } from '@/components/composed/ConsultingForm/types'
import { createConsultingRequestBody } from '@/components/composed/ConsultingForm/utils'
import { useApplyFinancingOffer } from '@/components/composed/financing/hooks'
import type { IConsultingFormProps } from '@/components/composed/finansingConsulting/ConsultingForm/types'
import { Button } from '@/components/ui'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { TextInput } from '@/components/ui/ReactHookFormFields'
import { getLocation } from '@/logic/app/reducer'
import { getUser, isUserAgrarian } from '@/logic/auth/reducer'
import { FINANCING_PRODUCT_BY_ROUTE } from '@/logic/financing/constants'
import type { RootState } from '@/redux/interfaces'
import type { EFinancingOfferRoute } from '@/types'
import { EFinancingOfferType, EFinancingProductId } from '@/types'
import { normalizePhoneNumber } from '@/utils/formatUtils'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { useApplyingFinancingOfferForm } from '@/utils/hooks/applyingFinancingOffer/useApplyingFinancingOfferForm'
import { messages } from './messages'
import './styles.scss'

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
  user: getUser()(state),
  location: getLocation()(state),
})

export const ConsultingForm: React.FC<IConsultingFormProps> = props => {
  const {
    submitButtonModifiers: submitButtonModifier = ['green'],
    inputPlaceholder = messages.form.phone.placeholder,
    inputModifiers,
    submitButtonClassname,
    className,
    children,
    requestMapper,
  } = props

  const { isAgrarian, user, location } = useSelector(mapState)

  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const { applyFinancingOffer, isLoading } = useApplyFinancingOffer()

  const { setOptions, profile } = useApplyingFinancingOfferForm({
    productId: EFinancingProductId.MARKETPLACE,
    offerType: EFinancingOfferType.MARKETPLACE,
    requestMapper,
  })

  const initialValues: IConsultingFormValues = {
    loanAmount: '',
    purpose: '',
    phone: profile.phone,
  }

  const financingProductId = useMemo(() => {
    return FINANCING_PRODUCT_BY_ROUTE[location.pathname as EFinancingOfferRoute]
  }, [location.pathname])

  const { control, handleSubmit, setValue } = useForm<IConsultingFormValues>({ defaultValues: initialValues })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setValue('phone', normalizePhoneNumber(profile.phone)), [profile.phone])

  const sendForm = useCallback(
    (values: IConsultingFormValues) => {
      const body = createConsultingRequestBody({ values, user, productId: financingProductId })

      if (isApplyingOfferRequestBody(body)) {
        const mappedBody = requestMapper?.(body) ?? body

        applyFinancingOffer({ body: mappedBody, withSuccessModal: true })
      }
    },
    [applyFinancingOffer, financingProductId, requestMapper, user],
  )

  const onSubmit = handleSubmit((values: IConsultingFormValues) => {
    if (isAuthenticated) {
      sendForm(values)
    } else {
      openAuthModal({
        shouldSendCodeImmediately: true,
        shouldSkipSelectRoleStep: true,
        initialValues: { phone: normalizePhoneNumber(values.phone) },
        onCloseDialog: () => {
          setOptions({ formValues: createConsultingRequestBody({ values, user, productId: financingProductId }) })
          sendForm(values)
        },
      })
    }
  })

  return (
    <form className={classNames(className, 'consultingForm')} onSubmit={onSubmit}>
      <TextInput
        name="phone"
        type="tel"
        placeholder={inputPlaceholder}
        rules={{ required: messages.form.phone.errors.required }}
        control={control}
        modifiers={inputModifiers}
        shouldBeTouchedBeforeError={false}
        isReadonly={isAuthenticated}
        isRequired
      />
      {children}
      <Button
        className={submitButtonClassname}
        type="submit"
        modifiers={submitButtonModifier}
        isDisabled={!isAgrarian && isAuthenticated}
        loaderProps={{ isVisible: isLoading }}
      >
        {messages.form.submit}
      </Button>
    </form>
  )
}
